import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  type ColumnDef,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useAdminApiQuery } from '@/hooks/use-admin-api-query';
import { qk } from '@/lib/query/keys';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { DataTable } from '@/components/super-admin/data-table';
import { WorkspaceMembersList } from '@/components/super-admin/workspace-members-list';
import { DetailLoadError } from '@/components/super-admin/detail-load-error';
import { WorkspaceBillingCard } from '@/components/super-admin/workspace-billing-card';
import { ChevronRight, ChevronLeft } from 'lucide-react';
import { cn } from '@/lib/utils';
import { PageContainer } from '@/components/page-container';
import { unitLabel, unitTitle } from '@/lib/usage-unit';

interface Member {
  id: string;
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  createdAt: string;
}

interface Channel {
  id: string;
  type: 'whatsapp' | 'instagram' | 'facebook';
  displayName: string;
  metaWabaId: string;
  metaBusinessId: string;
  whatsappWabaName: string;
  whatsappBusinessName: string;
  whatsappPhoneNumberId: string;
  whatsappDisplayPhoneNumber: string;
  maskedToken: string;
  tokenStatus: string;
  tokenExpiresAt: string | null;
  metaConnected: boolean;
  forwardingEnabled: boolean;
  createdAt: string;
  lastRefreshedAt: string | null;
  consecutiveFailures: number;
}

interface Subscription {
  id: string;
  planSlug: string;
  status: string;
  stripeSubscriptionId: string | null;
  currentPeriodEnd: string | null;
}

interface Usage {
  /** Usage this quota period, in `usageUnit` (AIT-436). */
  used: number;
  usageUnit: 'messages' | 'actions';
  orgPoolLimit: number;
  orgPoolSharePercentage: number;
  channels: { channelId: string; name: string; total: number; percentage: number }[];
}

interface WorkspaceDetail {
  workspace: { id: string; name: string; createdAt: string };
  members: Member[];
  channels: Channel[];
  subscription: Subscription | null;
  usage: Usage;
}

export function SuperAdminWorkspaceDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  // Set when arriving from the Channels search page (?channel=ch_…): scroll the
  // matching row into view and flash a highlight so the operator lands on it.
  const focusChannelId = searchParams.get('channel');

  const { data = null, isLoading, isError, error, refetch } = useAdminApiQuery<WorkspaceDetail>(
    qk.superAdmin('workspaces', id),
    `/super-admin/workspaces/${id}`,
  );
  const focusRowRef = useRef<HTMLTableRowElement | null>(null);
  // Brief highlight on arrival from the Channels search page, then fade — so the
  // row draws the eye without leaving the table permanently tinted.
  const [highlight, setHighlight] = useState(false);
  const [channelSearch, setChannelSearch] = useState('');

  // Memoized: an unmemoized array/map remounts every cell on each render.
  const channelRows = useMemo(() => data?.channels ?? [], [data]);
  const usedByChannel = useMemo(
    () => new Map((data?.usage.channels ?? []).map((c) => [c.channelId, c.total])),
    [data],
  );

  const channelColumns = useMemo<ColumnDef<Channel>[]>(
    () => [
      {
        accessorKey: 'type',
        header: 'Type',
        enableSorting: true,
        enableGlobalFilter: false,
        meta: { headerClassName: 'w-[110px]', sortLabel: 'type' },
        cell: ({ row }) =>
          row.original.type === 'instagram' ? 'Instagram' : row.original.type === 'facebook' ? 'Facebook' : 'WhatsApp',
      },
      {
        accessorKey: 'displayName',
        header: 'Name',
        enableSorting: true,
        meta: { cellClassName: 'font-medium', sortLabel: 'name' },
      },
      {
        accessorKey: 'forwardingEnabled',
        header: 'Messaging',
        enableSorting: true,
        enableGlobalFilter: false,
        meta: { headerClassName: 'w-[110px]', sortLabel: 'messaging' },
        cell: ({ row }) => (
          <span
            className={
              row.original.forwardingEnabled ? 'text-green-600' : 'text-muted-foreground'
            }
          >
            {row.original.forwardingEnabled ? 'Enabled' : 'Disabled'}
          </span>
        ),
      },
      {
        id: 'usage',
        accessorFn: (channel) => usedByChannel.get(channel.id) ?? 0,
        // Named after the org's meter: the raw number alone does not say
        // whether it counts messages or actions (CodeRabbit, PR #222).
        header: unitTitle(data?.usage.usageUnit),
        enableSorting: true,
        enableGlobalFilter: false,
        meta: {
          headerClassName: 'w-[140px]',
          cellClassName: 'tabular-nums',
          sortLabel: 'usage',
        },
      },
      {
        id: 'link',
        header: '',
        enableGlobalFilter: false,
        meta: { headerClassName: 'w-[110px]' },
        cell: ({ row }) => (
          <Link
            to={`/super-admin/channels/${row.original.id}`}
            className="text-sm text-primary hover:underline"
          >
            View details
          </Link>
        ),
      },
    ],
    [usedByChannel, data?.usage.usageUnit],
  );

  const channelTable = useReactTable({
    data: channelRows,
    columns: channelColumns,
    getRowId: (row) => row.id,
    defaultColumn: { enableSorting: false },
    enableMultiSort: false,
    sortDescFirst: false,
    state: { globalFilter: channelSearch },
    onGlobalFilterChange: setChannelSearch,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  useEffect(() => {
    if (!data || !focusChannelId) return;
    focusRowRef.current?.scrollIntoView({ block: 'center' });
    // Arrival flash: on when the deep-linked row appears, fades via the timeout below.
    setHighlight(true);
    const t = setTimeout(() => setHighlight(false), 2200);
    return () => clearTimeout(t);
  }, [data, focusChannelId]);

  if (isError) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-1 text-sm text-muted-foreground">
          <Link to="/super-admin" className="hover:text-foreground">
            Workspaces
          </Link>
          <ChevronRight className="h-3 w-3" />
          <span>...</span>
        </div>
        <DetailLoadError
          error={error}
          entity="Workspace"
          backTo="/super-admin"
          backLabel="Back To Workspaces"
          onRetry={() => void refetch()}
        />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-1 text-sm text-muted-foreground">
          <Link to="/super-admin" className="hover:text-foreground">
            Workspaces
          </Link>
          <ChevronRight className="h-3 w-3" />
          <Skeleton className="h-4 w-24" />
        </div>
        <Skeleton className="h-7 w-48" />
        <div className="mt-4">
          <Skeleton className="h-10 w-72" />
        </div>
        <Card>
          <CardContent className="space-y-3 py-4">
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!data) return null;

  return (
    <PageContainer variant="fluid" className="space-y-4">
      <Button
        variant="ghost"
        size="sm"
        className="-ml-2"
        onClick={() => navigate('/super-admin')}
      >
        <ChevronLeft className="h-4 w-4" />
        Back to workspaces
      </Button>

      <div className="flex items-center gap-1 text-sm text-muted-foreground">
        <Link to="/super-admin" className="hover:text-foreground">
          Workspaces
        </Link>
        <ChevronRight className="h-3 w-3" />
        <span>{data.workspace.name}</span>
      </div>

      <div className="flex items-center gap-2">
        <h1 className="text-xl font-semibold">
          Workspace: {data.workspace.name}
        </h1>
        <Badge variant="secondary" className="capitalize">
          {data.subscription?.planSlug ?? 'free'} plan
        </Badge>
      </div>

      <Tabs defaultValue="channels">
        <TabsList>
          <TabsTrigger value="channels">Channels</TabsTrigger>
          {/* value stays "members" (wire/URL key + test selector); label is "Users" per UX */}
          <TabsTrigger value="members">Users</TabsTrigger>
          <TabsTrigger value="usage">Usage</TabsTrigger>
          <TabsTrigger value="billing">Billing</TabsTrigger>
        </TabsList>

        <TabsContent value="channels" className="mt-4">
          {data.channels.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <h3 className="text-xl font-semibold">No channels connected</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                This workspace has no connected channels.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <span className="text-sm text-muted-foreground">
                  Total usage
                </span>
                <span className="text-base font-semibold tabular-nums">
                  {data.usage.used.toLocaleString()} / {data.usage.orgPoolLimit.toLocaleString()}
                </span>
                <span className="text-sm text-muted-foreground">
                  {unitLabel(data.usage.usageUnit)}
                </span>
                <div className="h-2 w-40 overflow-hidden rounded-full bg-muted">
                  <div
                    className={`h-full ${data.usage.orgPoolSharePercentage >= 100 ? 'bg-destructive' : 'bg-primary'}`}
                    style={{ width: `${Math.min(100, data.usage.orgPoolSharePercentage)}%` }}
                  />
                </div>
                <span className="text-sm text-muted-foreground">
                  {data.usage.orgPoolSharePercentage}% of org pool
                </span>
              </div>

              <Input
                placeholder="Search channels by name..."
                aria-label="Search channels"
                value={channelSearch}
                onChange={(e) => setChannelSearch(e.target.value)}
                className="max-w-sm"
              />

              <div className="rounded-md border">
                <DataTable
                  table={channelTable}
                  emptyState="No channels match that search."
                  rowRef={(row) =>
                    row.original.id === focusChannelId ? focusRowRef : undefined
                  }
                  rowClassName={(row) =>
                    cn(
                      'transition-colors duration-1000',
                      row.original.id === focusChannelId && highlight && 'bg-blue-500/10',
                    )
                  }
                />
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="members" className="mt-4">
          <WorkspaceMembersList members={data.members} />
        </TabsContent>

        <TabsContent value="usage" className="mt-4">
          <Card>
            <CardContent className="space-y-3 py-4">
              <div className="flex items-center gap-3">
                <span className="text-lg font-semibold tabular-nums">
                  {data.usage.used.toLocaleString()} / {data.usage.orgPoolLimit.toLocaleString()}
                </span>
                <span className="text-sm text-muted-foreground">
                  {unitLabel(data.usage.usageUnit)}
                </span>
                <div className="h-2 w-40 overflow-hidden rounded-full bg-muted">
                  <div
                    className={`h-full ${data.usage.orgPoolSharePercentage >= 100 ? 'bg-destructive' : 'bg-primary'}`}
                    style={{ width: `${Math.min(100, data.usage.orgPoolSharePercentage)}%` }}
                  />
                </div>
                <span className="text-sm text-muted-foreground">
                  {data.usage.orgPoolSharePercentage}% of org pool
                </span>
              </div>
              {data.usage.channels.map((c) => (
                <div key={c.channelId} className="flex justify-between text-sm">
                  <span>{c.name}</span>
                  <span className="tabular-nums">
                    {c.total.toLocaleString()} {unitLabel(data.usage.usageUnit)}
                  </span>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="billing" className="mt-4">
          <WorkspaceBillingCard
              subscription={data.subscription}
              workspacePublicId={id ?? ''}
              onChanged={() =>
                // Prefix key: refreshes this detail AND the workspaces list
                // (plan changes show on both).
                void queryClient.invalidateQueries({
                  queryKey: qk.superAdmin('workspaces'),
                })
              }
            />
        </TabsContent>
      </Tabs>
    </PageContainer>
  );
}
